<template>
  <div
    v-if="!shouldShowAddCommentForm"
    class="rounded border-static-default-low mb-24 border border-solid p-12 md:grid md:grid-cols-2 md:gap-2"
  >
    <div class="mb-4 md:mb-0">
      {{ i18n(translations.infoText) }}
    </div>

    <div class="md:gap-2">
      <RevButton
        class="m-auto w-full md:w-auto"
        size="small"
        variant="secondary"
        @click="shouldShowAddCommentForm = true"
      >
        {{ i18n(translations.addCommentButton) }}
      </RevButton>
    </div>
  </div>

  <RevCard v-else class="mb-24 p-32">
    <div class="mb-4 flex items-center">
      <h3 class="heading-3">{{ i18n(translations.addCommentFormTitle) }}</h3>
    </div>

    <div class="mb-12 flex items-center">
      <IconInfo class="mr-2" />
      <span> {{ i18n(translations.addCommentFormDescription) }}</span>
    </div>

    <div class="md:flex">
      <RevInputSelect
        id="mainMarket"
        v-model="selectedMarket"
        class="mb-4 w-full lg:mr-4 lg:w-[26.8rem]"
        :label="i18n(translations.addFormMarketSelectLabel)"
        :options="markets"
        :value="selectedMarket"
      />
      <RevInputText
        id="comment"
        v-model="comment"
        class="mb-2 w-full"
        :label="i18n(translations.addFormMarketCommentInputLabel)"
        :maxlength="maxLength"
      />
    </div>

    <div class="mb-4 md:text-right">
      <span class="body-2">
        {{
          i18n(translations.commentFieldLenghtLimit, {
            length: maxLength - comment.length,
          })
        }}
      </span>
    </div>

    <div class="flex gap-12">
      <RevButton
        :disabled="!canSubmit"
        full-width="adaptive"
        :loading="isSendingComment"
        type="submit"
        variant="secondary"
        @click="handleSubmit"
      >
        {{ i18n(translations.addCommentFormAddButton) }}
      </RevButton>

      <RevButton
        full-width="adaptive"
        variant="secondaryDestructive"
        @click="handleCancelForm"
      >
        {{ i18n(translations.addCommentFormCancelButton) }}
      </RevButton>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { IconInfo } from '@ds/icons/IconInfo'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import translations from '../Comments.translations'

const maxLength = 500

const props = defineProps<{
  listingId: number
}>()

const emit = defineEmits(['comment-submitted'])

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()
const { activeMarkets } = useSellerInfoStore()

const shouldShowAddCommentForm = ref(false)
const selectedMarket = ref('')
const comment = ref('')
const isSendingComment = ref(false)

const markets = computed(() =>
  activeMarkets.map((market: string) => ({
    label: i18n.country(market),
    value: market,
  })),
)

const canSubmit = computed(() => Boolean(selectedMarket.value && comment.value))

async function handleSubmit() {
  try {
    isSendingComment.value = true

    await $httpFetch(merchantsAPI.addListingComment, {
      body: { comment: comment.value },
      pathParams: {
        listingId: props.listingId,
        market: selectedMarket.value,
      },
    })

    openSuccessToast({
      title: i18n(translations.toastSuccessTitle),
      content: i18n(translations.toastSuccessContent),
    })

    emit('comment-submitted')
    shouldShowAddCommentForm.value = false
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isSendingComment.value = false
  }
}

async function handleCancelForm() {
  shouldShowAddCommentForm.value = false
}
</script>
